import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker, Circle } from '@react-google-maps/api';
import adminService from '../../services/adminService';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import './AddLocation.css';

const NEPAL_CENTER = { lat: 28.3949, lng: 84.1240 };

const AddLocation = () => {
  const navigate = useNavigate();
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  });

  // Location form state
  const [form, setForm] = useState({
    name: '',
    type: 'cultural',
    province: '',
    district: '',
    description: '',
    facts: ['', '', ''],
    hints: ['', '', ''],
  });

  // Map state
  const [pickedPoint, setPickedPoint] = useState(null);
  const [scanRadius, setScanRadius] = useState(10);
  const [scanning, setScanning] = useState(false);
  const [dropPoints, setDropPoints] = useState([]);
  const [scanError, setScanError] = useState('');

  // Street View preview
  const [previewIdx, setPreviewIdx] = useState(null);
  const svPreviewRef = useRef(null);

  // Saving
  const [saving, setSaving] = useState(false);

  const handleMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setPickedPoint({ lat, lng });
    setDropPoints([]);
    setPreviewIdx(null);
    setScanError('');
  }, []);

  const handleScan = async () => {
    if (!pickedPoint) return;
    setScanning(true);
    setScanError('');
    setDropPoints([]);
    setPreviewIdx(null);

    try {
      const data = await adminService.scanStreetView(pickedPoint.lat, pickedPoint.lng, scanRadius);
      setDropPoints(data.drop_points || []);
      if (data.count < 2) {
        setScanError(`Only ${data.count} Street View point(s) found. Need at least 2. Try a different location or increase radius.`);
      }
    } catch (err) {
      setScanError('Failed to scan. Check your connection and try again.');
      console.error(err);
    } finally {
      setScanning(false);
    }
  };

  const handlePreview = (idx) => {
    setPreviewIdx(idx);
    setTimeout(() => {
      if (svPreviewRef.current && window.google) {
        const dp = dropPoints[idx];
        const pos = { lat: dp.latitude, lng: dp.longitude };
        const svService = new window.google.maps.StreetViewService();
        const svOptions = {
          pov: { heading: 165, pitch: 0 },
          zoom: 0,
          addressControl: false,
          showRoadLabels: false,
          linksControl: true,
          panControl: true,
          zoomControl: false,
          fullscreenControl: false,
          enableCloseButton: false,
        };
        // Each scanned drop point already carries a verified pano_id from the
        // backend (it's only added if Street View imagery exists there). Use it
        // directly so the preview shows the exact panorama the game will use.
        // Re-resolving by rounded lat/lng can miss and fall back to a bare
        // position, which renders "No Street View available for this location".
        if (dp.pano_id) {
          new window.google.maps.StreetViewPanorama(svPreviewRef.current, { ...svOptions, pano: dp.pano_id });
          return;
        }
        const radii = [1000, 5000, 10000, 50000];
        let attempt = 0;
        const tryNext = () => {
          if (!svPreviewRef.current) return;
          if (attempt >= radii.length) {
            new window.google.maps.StreetViewPanorama(svPreviewRef.current, { ...svOptions, position: pos });
            return;
          }
          svService.getPanorama({ location: pos, radius: radii[attempt] }, (data, status) => {
            if (status === 'OK' && svPreviewRef.current) {
              new window.google.maps.StreetViewPanorama(svPreviewRef.current, { ...svOptions, pano: data.location.pano });
            } else {
              attempt++;
              tryNext();
            }
          });
        };
        tryNext();
      }
    }, 100);
  };

  const handleRemoveDropPoint = (idx) => {
    setDropPoints(prev => prev.filter((_, i) => i !== idx));
    if (previewIdx === idx) setPreviewIdx(null);
    else if (previewIdx > idx) setPreviewIdx(previewIdx - 1);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Please enter a location name.');
    if (dropPoints.length < 2) return alert('Need at least 2 drop points.');
    if (!pickedPoint) return alert('Pick a location on the map first.');

    setSaving(true);
    try {
      // 1. Create location
      const hints = form.hints.filter(h => h.trim());
      const facts = form.facts.filter(f => f.trim());
      const locationData = {
        name: form.name,
        type: form.type,
        province: form.province,
        district: form.district,
        latitude: pickedPoint.lat,
        longitude: pickedPoint.lng,
        description: form.description,
        facts: facts.length > 0 ? facts : undefined,
        hints: hints.length > 0 ? hints : undefined,
      };

      const result = await adminService.createLocation(locationData);
      const locationId = result.location.id;

      // 2. Create drop points
      for (const dp of dropPoints) {
        await adminService.createDropLocation(locationId, {
          location_id: locationId,
          latitude: dp.latitude,
          longitude: dp.longitude,
          description: dp.description || '',
        });
      }

      alert(`Location "${form.name}" created with ${dropPoints.length} drop points!`);
      navigate('/admin/locations');
    } catch (err) {
      console.error(err);
      alert('Failed to save location. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  const updateHint = (idx, val) => {
    setForm(prev => {
      const hints = [...prev.hints];
      hints[idx] = val;
      return { ...prev, hints };
    });
  };

  const updateFact = (idx, val) => {
    setForm(prev => {
      const facts = [...prev.facts];
      facts[idx] = val;
      return { ...prev, facts };
    });
  };

  const updateDropDesc = (idx, val) => {
    setDropPoints(prev => prev.map((dp, i) => i === idx ? { ...dp, description: val } : dp));
  };

  if (!isLoaded) {
    return <div className="add-location container"><p>Loading Google Maps...</p></div>;
  }

  return (
    <div className="add-location container">
      <div className="management-header">
        <h1>Add New Location</h1>
        <Button onClick={() => navigate('/admin/locations')} variant="secondary">← Back</Button>
      </div>

      <div className="add-location-grid">
        {/* Left: Map */}
        <div className="map-section">
          <Card title="1. Pick a point on the map">
            <GoogleMap
              mapContainerClassName="admin-map"
              center={pickedPoint || NEPAL_CENTER}
              zoom={pickedPoint ? 13 : 7}
              onClick={handleMapClick}
              options={{
                streetViewControl: false,
                mapTypeControl: true,
                fullscreenControl: false,
              }}
            >
              {pickedPoint && (
                <>
                  <Marker position={pickedPoint} />
                  <Circle
                    center={pickedPoint}
                    radius={scanRadius}
                    options={{
                      fillColor: '#4A90D9',
                      fillOpacity: 0.1,
                      strokeColor: '#4A90D9',
                      strokeOpacity: 0.4,
                      strokeWeight: 1,
                    }}
                  />
                </>
              )}
              {dropPoints.map((dp, i) => (
                <Marker
                  key={i}
                  position={{ lat: dp.latitude, lng: dp.longitude }}
                  icon={{
                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 24 32">
                        <path d="M12 0C5.4 0 0 5.4 0 12c0 6.6 12 20 12 20s12-13.4 12-20C24 5.4 18.6 0 12 0z" fill="${previewIdx === i ? '#2ECC71' : '#E67E22'}" stroke="#fff" stroke-width="2"/>
                        <text x="12" y="16" text-anchor="middle" fill="#fff" font-size="12" font-weight="bold">${i + 1}</text>
                      </svg>
                    `),
                    scaledSize: new window.google.maps.Size(24, 32),
                    anchor: new window.google.maps.Point(12, 32),
                  }}
                  onClick={() => handlePreview(i)}
                />
              ))}
            </GoogleMap>

            {pickedPoint && (
              <div className="scan-controls">
                <div className="picked-info">
                  Selected: {pickedPoint.lat.toFixed(4)}, {pickedPoint.lng.toFixed(4)}
                </div>
                <div className="radius-control">
                  <label>Scan radius:</label>
                  <select value={scanRadius} onChange={(e) => setScanRadius(Number(e.target.value))}>
                    <option value={10}>10 m</option>
                    <option value={50}>50 m</option>
                    <option value={100}>100 m</option>
                    <option value={200}>200 m</option>
                    <option value={500}>500 m</option>
                    <option value={1000}>1 km</option>
                  </select>
                </div>
                <Button onClick={handleScan} loading={scanning} variant="primary">
                  {scanning ? 'Scanning...' : 'Scan for Street View'}
                </Button>
              </div>
            )}

            {scanError && <div className="scan-error">{scanError}</div>}

            {dropPoints.length > 0 && (
              <div className="drop-points-list">
                <h4>Found {dropPoints.length} drop point(s) {dropPoints.length >= 2 ? '✓' : '— need at least 2'}</h4>
                {dropPoints.map((dp, i) => (
                  <div key={i} className={`drop-point-item ${previewIdx === i ? 'active' : ''}`}>
                    <div className="dp-info" onClick={() => handlePreview(i)}>
                      <span className="dp-num">{i + 1}</span>
                      <span className="dp-coords">{dp.latitude}, {dp.longitude}</span>
                    </div>
                    <input
                      type="text"
                      className="dp-desc-input"
                      placeholder="Description (e.g., Main road)"
                      value={dp.description || ''}
                      onChange={(e) => updateDropDesc(i, e.target.value)}
                    />
                    <button className="dp-remove" onClick={() => handleRemoveDropPoint(i)} title="Remove">×</button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Street View Preview */}
          {previewIdx !== null && (
            <Card title={`Street View Preview — Point ${previewIdx + 1}`}>
              <div className="sv-preview" ref={svPreviewRef} />
            </Card>
          )}
        </div>

        {/* Right: Form */}
        <div className="form-section">
          <Card title="2. Location Details">
            <div className="form-group">
              <label>Name *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Pashupatinath Temple"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Type *</label>
                <select
                  className="form-select"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                >
                  <option value="cultural">Cultural</option>
                  <option value="city">City</option>
                  <option value="village">Village</option>
                  <option value="landmark">Landmark</option>
                  <option value="highway">Highway</option>
                </select>
              </div>

              <div className="form-group">
                <label>Province</label>
                <select
                  className="form-select"
                  value={form.province}
                  onChange={(e) => setForm({ ...form, province: e.target.value })}
                >
                  <option value="">Select Province</option>
                  <option value="Province No. 1">Province No. 1</option>
                  <option value="Madhesh">Madhesh</option>
                  <option value="Bagmati">Bagmati</option>
                  <option value="Gandaki">Gandaki</option>
                  <option value="Lumbini">Lumbini</option>
                  <option value="Karnali">Karnali</option>
                  <option value="Sudurpashchim">Sudurpashchim</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>District</label>
              <Input
                value={form.district}
                onChange={(e) => setForm({ ...form, district: e.target.value })}
                placeholder="e.g., Kathmandu"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                className="form-textarea"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description of the location..."
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Hints (shown to players)</label>
              {form.hints.map((hint, i) => (
                <Input
                  key={i}
                  value={hint}
                  onChange={(e) => updateHint(i, e.target.value)}
                  placeholder={`Hint ${i + 1}`}
                  style={{ marginBottom: '0.5rem' }}
                />
              ))}
            </div>

            <div className="form-group">
              <label>Facts (educational — shown after each round)</label>
              {form.facts.map((fact, i) => (
                <Input
                  key={i}
                  value={fact}
                  onChange={(e) => updateFact(i, e.target.value)}
                  placeholder={`Fact ${i + 1}`}
                  style={{ marginBottom: '0.5rem' }}
                />
              ))}
            </div>
          </Card>

          <Card title="3. Save">
            <div className="save-summary">
              <p><strong>Location:</strong> {form.name || '—'}</p>
              <p><strong>Coords:</strong> {pickedPoint ? `${pickedPoint.lat.toFixed(4)}, ${pickedPoint.lng.toFixed(4)}` : 'Not selected'}</p>
              <p><strong>Drop Points:</strong> {dropPoints.length} {dropPoints.length >= 2 ? '✓' : '(need 2+)'}</p>
            </div>
            <Button
              variant="primary"
              size="lg"
              onClick={handleSave}
              loading={saving}
              disabled={!form.name.trim() || dropPoints.length < 2 || !pickedPoint || saving}
              style={{ width: '100%' }}
            >
              {saving ? 'Saving...' : 'Save Location'}
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AddLocation;
